import React, { useState, useEffect } from 'react';
import { ProjectTwoTone, StarTwoTone, WarningTwoTone, BulbTwoTone, FireTwoTone, RocketTwoTone, MoneyCollectTwoTone, HeartTwoTone, BankTwoTone, TeamOutlined, CompassTwoTone, FieldTimeOutlined, PushpinTwoTone, ThunderboltTwoTone, EyeTwoTone, FileTextTwoTone, CheckCircleTwoTone, CloseCircleTwoTone, SafetyCertificateTwoTone } from '@ant-design/icons';
import { Form, DatePicker, TimePicker, Select, Button, Switch, Alert, Row, Col, Typography, Modal, Input } from 'antd';

import dayjs from 'dayjs';
import '../../App.css';

import { BaziChartGrid } from '../../components/BaziChartGrid';

import tenGodsProfiles from '../../data/ten_gods_profiles.json';

const { Title, Text } = Typography;
interface Pillar {
  position: string;
  stem: string;
  branch: string;
  element?: string;
  pinyin?: string;
  ciongStem?: boolean;
  ciongBranch?: boolean;
}

interface StrategyGuide {
  keuangan: string;
  kesehatan: string;
  keluarga: string;
  partner: string;
  pekerjaan: string;
  bisnis: string;
}

interface ClashItem {
  name: string;
  impact: string;
}

interface LuckCycle {
  stem: string;
  branch: string;
  pinyin: string;
  element: string;
  elementCh: string;
  start_age: number;
  end_age: number;
  start_year: number;
  end_year: number;
}

interface ChartData {
  chart_id?: string;
  date_of_birth?: string;
  time_of_birth?: string;
  gender?: string;
  pillars?: Pillar[];
  day_master_strength?: string;
  day_master_scores?: { total_score?: number; hour_stem_score?: number; day_stem_score?: number; month_stem_score?: number; year_stem_score?: number };
  pilar_bumi_scores?: { total_score?: number; hour_branch_score?: number; day_branch_score?: number; month_branch_score?: number; year_branch_score?: number };
  unknown_tob?: boolean;
  bazi_compatibility_score?: number;
  bazi_compatibility_label?: string;
  ten_relations?: {
    year_stem?: string;
    month_stem?: string;
    hour_stem?: string;
  };
  luck_cycles?: LuckCycle[];
  kua?: { 
    kua_number: number; 
    trigram: string; 
    group: string;
    directions?: {
      favorable?: Record<string, { direction: string; name: string; meaning: string }>;
      unfavorable?: Record<string, { direction: string; name: string; meaning: string }>;
    };
  };
  career_health?: { 
    element?: { name?: string };
    info?: { karakteristik?: string; kinerja?: string };
    details?: { element_pendukung?: string[]; karir?: string[]; penyakit?: string[] };
  };
  strategy_guide?: StrategyGuide;
  internal_clashes?: ClashItem[];
  elements?: Record<string, number>;
}

interface PersonalChartProps {
  token: string;
  API_URL: string;
}

export const PersonalChart: React.FC<PersonalChartProps> = ({
  token,
  API_URL
}) => {
  const [loading, setLoading] = useState(false);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [calcError, setCalcError] = useState('');


  const [form] = Form.useForm();
  const [unknownTob, setUnknownTob] = useState(false);



  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/personal/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.chart) {
            setChart(result.chart);
          }
          if (result.user) {
            form.setFieldsValue({
              name: result.user.name || '',
              gender: result.user.gender || 'male',
              timezone: result.user.bazi_birth_timezone || 'Asia/Jakarta',
              dob: result.user.bazi_birth_date ? dayjs(result.user.bazi_birth_date) : undefined,
              tob: result.user.bazi_birth_time && result.user.bazi_birth_time !== '00:00:00' 
                ? dayjs(result.user.bazi_birth_time, 'HH:mm:ss') 
                : undefined
            });
            if (result.user.bazi_birth_time === '00:00:00') {
               setUnknownTob(true);
            }
          }
        } else if (res.status === 401) {
          localStorage.removeItem('personal_token');
          localStorage.removeItem('personal_email');
          window.location.href = '/login';
          return;
        }
      } catch (err) {
        console.error('Failed to fetch PERSONAL profile:', err);
      }
    };
    fetchProfile();
  }, [token, API_URL, form]);

  // Custom Transit / Comparison states
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');
  const [compareResult, setCompareResult] = useState<any>(null);
  const [compareChartData, setCompareChartData] = useState<ChartData | null>(null);

  // Modal Visibility States
  const [isMainChartVisible, setIsMainChartVisible] = useState(false);
  const [isCompareChartVisible, setIsCompareChartVisible] = useState(false);
  const [compareUnknownTob, setCompareUnknownTob] = useState(false);
  const [compareForm] = Form.useForm();

  const [activeTabKey, setActiveTabKey] = useState('1');

  const handleCompare = async (values: any) => {
    setCompareLoading(true);
    setCompareError('');
    try {
      if (!values || !values.dob) {
        throw new Error('Silakan pilih tanggal pembanding terlebih dahulu.');
      }
      if (!chart?.chart_id) {
        throw new Error('Bagan BaZi kamu belum dihitung atau tidak valid.');
      }

      const transitDobStr = typeof values.dob.format === 'function' ? values.dob.format('YYYY-MM-DD') : String(values.dob);
      const transitTobStr = compareUnknownTob ? '00:00' : (values.tob ? (typeof values.tob.format === 'function' ? values.tob.format('HH:mm') : String(values.tob)) : '12:00');
      const tzStr = values.timezone || form.getFieldValue('timezone') || 'Asia/Jakarta';

      // Ambil data kelahiran user dari form / chart
      const formVals = form.getFieldsValue();
      const userDob = chart.date_of_birth || (formVals.dob ? (typeof formVals.dob.format === 'function' ? formVals.dob.format('YYYY-MM-DD') : String(formVals.dob)) : '');
      const userTob = chart.time_of_birth || (formVals.tob ? (typeof formVals.tob.format === 'function' ? formVals.tob.format('HH:mm') : String(formVals.tob)) : '12:00');
      const userGender = chart.gender || formVals.gender || 'male';
      const userName = formVals.name || 'Saya';

      // Hitung ulang chart user dengan transit_date
      const res = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_of_birth: userDob,
          time_of_birth: compareUnknownTob ? '00:00' : userTob,
          timezone: tzStr,
          gender: userGender,
          person_name: userName,
          unknown_tob: compareUnknownTob,
          transit_date: transitDobStr,
          transit_time: transitTobStr
        })
      });

      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('personal_token');
          localStorage.removeItem('personal_email');
          window.location.href = '/login';
          return;
        }
        const errorData = await res.json();
        throw new Error(errorData.error || 'Gagal menghitung keselarasan tanggal');
      }

      const data = await res.json();
      const chartResult = data.data || data;

      setCompareResult({
        bazi_synergy_score: chartResult.bazi_compatibility_score,
        bazi_synergy_label: chartResult.bazi_compatibility_label,
        strategy_guide: chartResult.strategy_guide
      });

      // Fetch custom date's own chart for "Lihat Bagan Pembanding"
      try {
        const customChartRes = await fetch(`${API_URL}/api/v1/personal/charts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            date_of_birth: transitDobStr,
            time_of_birth: transitTobStr,
            timezone: tzStr,
            gender: userGender,
            person_name: 'Tanggal Custom',
            unknown_tob: compareUnknownTob
          })
        });
        if (customChartRes.ok) {
          const customChartData = await customChartRes.json();
          const customChartPayload = customChartData.data || customChartData;
          setCompareChartData({ ...customChartPayload, unknown_tob: compareUnknownTob });
        } else {
          // Fallback to transit-modified chart
          setCompareChartData({ ...chartResult, unknown_tob: compareUnknownTob });
        }
      } catch (_e) {
        setCompareChartData({ ...chartResult, unknown_tob: compareUnknownTob });
      }
    } catch (err: any) {
      setCompareError(err.message || 'Gagal melakukan pembandingan.');
    } finally {
      setCompareLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCalculate = async (values: any) => {
    setLoading(true);
    setCalcError('');
    try {
      if (!values || !values.dob) {
        throw new Error('Silakan pilih tanggal lahir kamu terlebih dahulu.');
      }

      const dobStr = typeof values.dob.format === 'function' ? values.dob.format('YYYY-MM-DD') : String(values.dob);
      const tobStr = unknownTob ? '00:00' : (values.tob ? (typeof values.tob.format === 'function' ? values.tob.format('HH:mm') : String(values.tob)) : '12:00');

      const res = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_of_birth: dobStr,
          time_of_birth: tobStr,
          timezone: (values && values.timezone) || 'Asia/Jakarta',
          gender: (values && values.gender) || 'male',
          person_name: (values && values.name) || 'Saya',
          unknown_tob: unknownTob
        })
      });

      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('personal_token');
          localStorage.removeItem('personal_email');
          window.location.href = '/login';
          return;
        }
        const errorData = await res.json();
        throw new Error(errorData.error || 'Gagal menghitung BaZi');
      }

      const data = await res.json();
      const chartPayload = data.data || data;
      setChart(chartPayload);
      setActiveTabKey('1');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err: any) {
      setCalcError(err.message || 'Koneksi ke server gagal.');
      console.error('Chart calculation error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const elIndoToEn: Record<string, string> = { 'KAYU': 'wood', 'API': 'fire', 'TANAH': 'earth', 'LOGAM': 'metal', 'AIR': 'water' };
  const pendukungElements = new Set<string>(
    (chart?.career_health?.details?.element_pendukung || [])
      .filter((e: string) => e && e.trim() !== '')
      .map((e: string) => elIndoToEn[e.trim().toUpperCase()] || '')
      .filter((e: string) => e !== '')
  );

  const adminEmails = ['admin@bazi.com', 'developer@bazi.com', 'support@bazi.com'];
  const userEmail = localStorage.getItem('personal_email') || '';
  const isAdmin = adminEmails.includes(userEmail.toLowerCase().trim());

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '12px' }}>
      {(!chart || isAdmin) && (
        <div style={{ width: '100%', border: '3px solid #1a202c', borderRadius: '12px', padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c', marginBottom: '20px' }}>
          <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
              <ProjectTwoTone style={{ marginRight: 8 }} /> Input Data Kelahiran
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
              Masukkan data kelahiran untuk menghitung Bagan BaZi.
            </p>
          </div>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCalculate}
            initialValues={{ gender: 'male', timezone: 'Asia/Jakarta' }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 800 }}>Nama</span>} name="name">
                  <Input placeholder="Nama kamu" style={{ borderRadius: '8px', border: '2px solid #1a202c', fontWeight: 600 }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 800 }}>Jenis Kelamin</span>} name="gender" rules={[{ required: true }]}>
                  <Select style={{ borderRadius: '8px', border: '2px solid #1a202c', fontWeight: 600 }}>
                    <Select.Option value="male">Laki-laki</Select.Option>
                    <Select.Option value="female">Perempuan</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Lahir</span>} name="dob" rules={[{ required: true, message: 'Silakan pilih tanggal lahir' }]}>
                  <DatePicker style={{ width: '100%', borderRadius: '8px', border: '2px solid #1a202c', fontWeight: 600 }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir</span>} name="tob">
                  <TimePicker style={{ width: '100%', borderRadius: '8px', border: '2px solid #1a202c', fontWeight: 600 }} format="HH:mm" minuteStep={5} disabled={unknownTob} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu</span>} name="timezone" rules={[{ required: true }]}>
                  <Select style={{ borderRadius: '8px', border: '2px solid #1a202c', fontWeight: 600 }}>
                    <Select.Option value="Asia/Jakarta">WIB (Asia/Jakarta)</Select.Option>
                    <Select.Option value="Asia/Makassar">WITA (Asia/Makassar)</Select.Option>
                    <Select.Option value="Asia/Jayapura">WIT (Asia/Jayapura)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label=" ">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
                    <Switch checked={unknownTob} onChange={setUnknownTob} />
                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#4a5568' }}>Jam Lahir Tidak Diketahui</span>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Button type="primary" htmlType="submit" loading={loading} style={{ fontWeight: 900, background: '#1a202c', color: '#fff', borderRadius: '8px', height: '44px', border: '3px solid #1a202c', boxShadow: '3px 3px 0 #1a202c', width: '100%' }}>
              Hitung Bagan BaZi
            </Button>
          </Form>
          {calcError && (
            <Alert type="error" message={calcError} showIcon closable onClose={() => setCalcError('')} style={{ marginTop: '12px', borderRadius: '8px', border: '2px solid #e03131', fontWeight: 700 }} />
          )}
        </div>
      )}

      {!chart ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '20vh', flexDirection: 'column', gap: '16px' }}>
          {loading ? (
            <>
              <div className="ant-spin-dot ant-spin-dot-spin" style={{ fontSize: 32 }}><i /><i /><i /><i /></div>
              <span style={{ fontWeight: 800, color: '#1a202c' }}>Memuat Bagan BaZi...</span>
            </>
          ) : null}
        </div>
      ) : (
        // Authentic Full BaZi Chart View
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>


          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <Title level={2} style={{ margin: 0, fontWeight: 900 }}>BaZi Chart 八字</Title>
            <Text style={{ fontWeight: 700, color: '#495057', fontSize: '1.1rem' }}>Bagan Lahir (Birth Chart)</Text>
          </div>

          {/* Navigation Tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', width: '100%', maxWidth: '100%', marginTop: '12px', marginBottom: '12px' }}>
            <Button
              onClick={() => setActiveTabKey('1')}
              style={{
                flex: 1,
                minHeight: '46px',
                height: 'auto',
                whiteSpace: 'normal',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid #1a202c',
                borderRadius: '10px',
                fontWeight: 900,
                fontSize: '1rem',
                backgroundColor: activeTabKey === '1' ? '#fab005' : '#ffffff',
                color: '#1a202c',
                boxShadow: activeTabKey === '1' ? 'none' : '3px 3px 0 #1a202c',
                transform: activeTabKey === '1' ? 'translate(2px, 2px)' : 'none',
                transition: 'all 0.1s ease'
              }}
            >
              <span style={{ whiteSpace: 'normal', display: 'inline-block', lineHeight: '1.2' }}>
                <><ProjectTwoTone style={{ marginRight: 8 }} /> Bagan Lahir & Analisis</>
              </span>
            </Button>
            <Button
              onClick={() => setActiveTabKey('2')}
              style={{
                flex: 1,
                minHeight: '46px',
                height: 'auto',
                whiteSpace: 'normal',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid #1a202c',
                borderRadius: '10px',
                fontWeight: 900,
                fontSize: '1rem',
                backgroundColor: activeTabKey === '2' ? '#fab005' : '#ffffff',
                color: '#1a202c',
                boxShadow: activeTabKey === '2' ? 'none' : '3px 3px 0 #1a202c',
                transform: activeTabKey === '2' ? 'translate(2px, 2px)' : 'none',
                transition: 'all 0.1s ease'
              }}
            >
              <span style={{ whiteSpace: 'normal', display: 'inline-block', lineHeight: '1.2' }}>
                <ProjectTwoTone style={{ marginRight: 8 }} /> Keselarasan Tanggal Custom
              </span>
            </Button>

          </div>

          {activeTabKey === '1' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>

          {/* Header Badges */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {chart.day_master_strength && (
              <span style={{ backgroundColor: chart.day_master_strength === 'Strong' ? '#ebfbee' : '#fff5f5', color: chart.day_master_strength === 'Strong' ? '#2b8a3e' : '#e03131', border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px', fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem' }}>
                Day Master: {chart.day_master_strength === 'Strong' ? 'DM Strength (Strong)' : 'DM Weak (Weak)'}
              </span>
            )}
            {chart.bazi_compatibility_score !== undefined && (() => {
              const lbl = (chart.bazi_compatibility_label || 'AVERAGE').toUpperCase();
              let bg = '#FFFF65'; let color = '#555000'; // AVERAGE
              if (lbl === 'VERY GOOD') { bg = '#e8f4fd'; color = '#0071C1'; } // VERY GOOD (Biru)
              if (lbl === 'GOOD') { bg = '#B5E6A2'; color = '#2d6b1e'; } // GOOD (Hijau)
              if (lbl === 'CHALLENGE') { bg = '#FF66FF'; color = '#990099'; } // CHALLENGE
              if (lbl === 'BAD') { bg = '#ffe6ee'; color = '#FF0068'; } // BAD
              return (
                <span style={{ backgroundColor: bg, color: color, border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px', fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem' }}>
                  Personal Alignment: {Math.round(Number(chart.bazi_compatibility_score || 0))}% ({chart.bazi_compatibility_label || 'AVERAGE'}) ⓘ
                </span>
              );
            })()}
            {chart.kua && (
              <span style={{ backgroundColor: '#e8f0fe', color: '#1a73e8', border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px', fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem' }}>
                Angka Kua: {chart.kua.kua_number} ({chart.kua.trigram} - {chart.kua.group})
              </span>
            )}
          </div>

          {/* Button to show chart */}
          <Button 
            type="primary" 
            onClick={() => setIsMainChartVisible(true)}
            style={{ 
              fontWeight: 900, background: '#1a202c', color: '#fff', 
              borderRadius: '8px', height: '46px', fontSize: '1rem',
              width: '100%', maxWidth: '100%',
              border: '3px solid #1a202c',
              boxShadow: '3px 3px 0 #1a202c'
            }}
          >
            <><ProjectTwoTone style={{ marginRight: 8 }} /> Lihat Bagan Lahir</>
          </Button>

          <Modal
            title={<span style={{ fontWeight: 900, fontSize: '1.2rem' }}>Bagan Lahir (Birth Chart)</span>}
            open={isMainChartVisible}
            onCancel={() => setIsMainChartVisible(false)}
            footer={null}
            width={800}
            centered
            bodyStyle={{ padding: '20px 0' }}
          >
            <div style={{ overflowX: 'auto', width: '100%', padding: '0 20px' }}>
              <div style={{ minWidth: '600px' }}>
                <BaziChartGrid chart={chart} unknownTob={unknownTob} />
              </div>
            </div>
          </Modal>

          {/* Footer Legends */}
          {pendukungElements.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: '#7c5c00', background: '#fff9db', border: '2px solid #f59f00', borderRadius: '10px', padding: '6px 14px', fontWeight: 700, boxShadow: '2px 2px 0 #1a202c', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 4, border: '3px solid #f59f00', boxShadow: '0 0 6px 2px rgba(245,159,0,0.5)', background: 'transparent', flexShrink: 0 }} />
              <span>
                <StarTwoTone twoToneColor="#eb2f96" style={{ marginRight: 8 }} /> <strong>Highlight Emas</strong> = Elemen Pendukung DM ({chart.day_master_strength === 'Strong' ? 'Kuat' : 'Lemah'}):&nbsp;
                {Array.from(pendukungElements).map(e => e.charAt(0).toUpperCase() + e.slice(1)).join(', ')}
              </span>
            </div>
          )}

          {/* Self-Clash warnings */}
          {chart.internal_clashes && chart.internal_clashes.length > 0 && (
            <div style={{ 
              width: '100%',
              maxWidth: '100%',
              border: '2px solid #fa5252', 
              borderRadius: '10px', 
              padding: '12px 16px', 
              backgroundColor: '#fff5f5',
              boxShadow: '3px 3px 0 #1a202c'
            }}>
              <strong style={{ color: '#fa5252', display: 'block', marginBottom: '8px', fontSize: '0.88rem' }}>
                <><WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} /> Peringatan Bentrokan Internal (Self-Clash):</>
              </strong>
              {chart.internal_clashes.map((clash, cIdx) => (
                <div key={cIdx} style={{ 
                  marginBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '8px' : '0',
                  borderBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '1px dashed #ffa8a8' : 'none',
                  paddingBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '6px' : '0'
                }}>
                  <strong style={{ fontSize: '0.84rem', color: '#c92a2a' }}>{clash.name}</strong>
                  <p style={{ fontSize: '0.78rem', color: '#495057', margin: '2px 0 0 0', fontWeight: 600 }}>{clash.impact}</p>
                </div>
              ))}
            </div>
          )}

          {/* Strategy Guide Grid */}
          {chart.strategy_guide && (
            <div style={{ 
              width: '100%',
              maxWidth: '100%',
              border: '3px solid #1a202c', 
              borderRadius: '12px', 
              padding: '20px', 
              backgroundColor: '#ffffff',
              boxShadow: '4px 4px 0 #1a202c'
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                  <><BulbTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> Panduan Strategi Buat Kamu</>
                </h3>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: '12px'
              }}>
                {[
                  { title: <><MoneyCollectTwoTone twoToneColor="#f59f00" style={{ marginRight: 4 }} /> Keuangan</>, text: chart.strategy_guide.keuangan },
                  { title: <><HeartTwoTone twoToneColor="#e03131" style={{ marginRight: 4 }} /> Kesehatan</>, text: chart.strategy_guide.kesehatan },
                  { title: <><TeamOutlined style={{ marginRight: 4, color: '#1098ad' }} /> Hubungan Keluarga</>, text: chart.strategy_guide.keluarga },
                  { title: <><TeamOutlined style={{ marginRight: 4, color: '#2b8a3e' }} /> Partner Bisnis</>, text: chart.strategy_guide.partner },
                  { title: <><BankTwoTone twoToneColor="#1971c2" style={{ marginRight: 4 }} /> Pekerjaan</>, text: chart.strategy_guide.pekerjaan },
                  { title: <><BankTwoTone twoToneColor="#d9480f" style={{ marginRight: 4 }} /> Bisnis</>, text: chart.strategy_guide.bisnis }
                ].map((item, idx) => (
                  <div 
                    key={idx} 
                    style={{ 
                      border: '2px solid #1a202c', 
                      borderRadius: '10px', 
                      padding: '12px 14px', 
                      backgroundColor: '#ffffff',
                      boxShadow: '2px 2px 0 #1a202c'
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1a202c', marginBottom: '4px' }}>{item.title}</div>
                    <p style={{ fontSize: '0.78rem', color: '#4a5568', margin: 0, lineHeight: '1.5', fontWeight: 600 }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Karakteristik & Kinerja Bawaan */}
          {chart.career_health?.info && (
            <div style={{ 
              width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                  <StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> Karakteristik & Gaya Kinerja Bawaan
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {chart.career_health.info.karakteristik && (
                  <div style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '12px 14px', backgroundColor: '#f8f9fa', boxShadow: '2px 2px 0 #1a202c' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1098ad', marginBottom: '4px' }}><PushpinTwoTone twoToneColor="#1098ad" style={{ marginRight: 4 }} /> Karakteristik Utama</div>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 600, whiteSpace: 'pre-line' }}>{chart.career_health.info.karakteristik}</p>
                  </div>
                )}
                {chart.career_health.info.kinerja && (
                  <div style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '12px 14px', backgroundColor: '#ebfbee', boxShadow: '2px 2px 0 #1a202c' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#2b8a3e', marginBottom: '4px' }}><ThunderboltTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Gaya Kinerja & Lingkungan Kerja Ideal</div>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 600, whiteSpace: 'pre-line' }}>{chart.career_health.info.kinerja}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analisis 10 Dewa (Ten Gods) & Bintang Dominan */}
          {chart.ten_relations && (() => {
            const relMap: Record<string, string> = {
              '比肩': 'F', '劫财': 'RW', '食神': 'EG', '伤官': 'HO', '偏财': 'IW',
              '正财': 'DW', '七杀': '7K', '正官': 'DO', '偏印': 'IR', '正印': 'DR'
            };

            const monthRel = chart.ten_relations.month_stem || '';
            const yearRel = chart.ten_relations.year_stem || '';
            const hourRel = chart.ten_relations.hour_stem || '';

            const dominantCode = relMap[monthRel] || relMap[yearRel] || relMap[hourRel] || 'DW';
            const profiles = tenGodsProfiles as Record<string, any>;
            const dominantProfile = profiles[dominantCode];

            const presentCodes = Array.from(new Set([relMap[monthRel], relMap[yearRel], relMap[hourRel]].filter(Boolean)));

            return (
              <div style={{ 
                width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
                padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
              }}>
                <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                  <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                    <ProjectTwoTone style={{ marginRight: 8 }} /> Profil 10 Dewa (Ten Gods) & Bintang Dominan
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
                    Karakter psikologis, bakat tersembunyi, dan kecocokan karir bawaan lahir.
                  </p>
                </div>

                {dominantProfile && (
                  <div style={{ 
                    border: '3px solid #fab005', 
                    borderRadius: '12px', 
                    padding: '16px', 
                    backgroundColor: '#fff9db', 
                    boxShadow: '3px 3px 0 #1a202c',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{ backgroundColor: '#fab005', color: '#1a202c', fontWeight: 950, fontSize: '0.75rem', padding: '3px 10px', borderRadius: '20px', border: '1.5px solid #1a202c' }}>
                        <><StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> BINTANG PALING DOMINAN (Struktur Utama)</>
                      </span>
                      <span style={{ fontWeight: 900, fontSize: '0.9rem', color: '#d9480f' }}>
                        {dominantProfile.name} ({dominantProfile.code})
                      </span>
                    </div>

                    <h4 style={{ margin: '4px 0 10px 0', fontSize: '1.2rem', fontWeight: 950, color: '#1a202c' }}>
                      {dominantProfile.title}
                    </h4>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ border: '1.5px solid #1a202c', borderRadius: '8px', padding: '10px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#1098ad', marginBottom: '2px' }}><PushpinTwoTone twoToneColor="#1098ad" style={{ marginRight: 4 }} /> Karakteristik Utama:</div>
                        <div style={{ fontSize: '0.78rem', color: '#2d3748', fontWeight: 600, lineHeight: '1.5' }}>{dominantProfile.karakteristik}</div>
                      </div>

                      <div style={{ border: '1.5px solid #1a202c', borderRadius: '8px', padding: '10px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#2b8a3e', marginBottom: '2px' }}><><RocketTwoTone twoToneColor="#52c41a" style={{ marginRight: 8 }} /> Potensi Kekuatan:</></div>
                        <div style={{ fontSize: '0.78rem', color: '#2d3748', fontWeight: 600, lineHeight: '1.5' }}>{dominantProfile.potensi}</div>
                      </div>

                      <div style={{ border: '1.5px solid #1a202c', borderRadius: '8px', padding: '10px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#1971c2', marginBottom: '2px' }}><BankTwoTone twoToneColor="#1971c2" style={{ marginRight: 4 }} /> Kecocokan Pekerjaan:</div>
                        <div style={{ fontSize: '0.78rem', color: '#2d3748', fontWeight: 650, lineHeight: '1.5' }}>{dominantProfile.pekerjaan}</div>
                      </div>

                      <div style={{ border: '1.5px solid #1a202c', borderRadius: '8px', padding: '10px', backgroundColor: '#ffffff' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#d9480f', marginBottom: '2px' }}><BankTwoTone twoToneColor="#d9480f" style={{ marginRight: 4 }} /> Kecocokan Usaha / Bisnis:</div>
                        <div style={{ fontSize: '0.78rem', color: '#2d3748', fontWeight: 650, lineHeight: '1.5' }}>{dominantProfile.usaha}</div>
                      </div>
                    </div>
                  </div>
                )}

                {presentCodes.length > 1 && (
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#4a5568', marginBottom: '10px' }}>
                      <><StarTwoTone twoToneColor="#eb2f96" style={{ marginRight: 8 }} /> Bintang Pendukung Lainnya dalam Bagan Kamu:</>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {presentCodes.filter(c => c !== dominantCode).map(code => {
                        const prof = profiles[code];
                        if (!prof) return null;
                        return (
                          <div key={code} style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '12px', backgroundColor: '#f8f9fa' }}>
                            <div style={{ fontWeight: 900, fontSize: '0.9rem', color: '#1a202c', marginBottom: '4px' }}>
                              {prof.name} ({prof.code}) – <span style={{ color: '#495057' }}>{prof.title}</span>
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#4a5568', fontWeight: 600, lineHeight: '1.5' }}>
                              {prof.karakteristik}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Peluang Bisnis & Pekerjaan Ideal */}
          {chart.career_health?.details?.karir && chart.career_health.details.karir.length > 0 && (
            <div style={{ 
              width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                  <BankTwoTone twoToneColor="#fa8c16" style={{ marginRight: 8 }} /> Peluang Bisnis & Pekerjaan Ideal
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {chart.career_health.details.karir.map((item, idx) => (
                  <div key={idx} style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '14px', backgroundColor: '#fff9db', boxShadow: '2px 2px 0 #1a202c' }}>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 650, whiteSpace: 'pre-line' }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sektor Arah Keberuntungan (Feng Shui Kua) */}
          {chart.kua?.directions && (
            <div style={{ 
              width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                  <CompassTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Sektor Arah Keberuntungan (Kua {chart.kua.kua_number} - {chart.kua.trigram})
                </h3>
              </div>

              {chart.kua.directions.favorable && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#2b8a3e', marginBottom: '8px' }}>                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Arah Keberuntungan (Favorable):</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                    {Object.entries(chart.kua.directions.favorable).map(([key, dir]) => (
                      <div key={key} style={{ border: '2px solid #2b8a3e', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#ebfbee' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#2b8a3e' }}>{dir.name} ({dir.direction})</div>
                        <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600 }}>{dir.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {chart.kua.directions.unfavorable && (
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#e03131', marginBottom: '8px' }}>                  <CloseCircleTwoTone twoToneColor="#f5222d" style={{ marginRight: 4 }} /> Arah Perlu Diwaspadai (Unfavorable):</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                    {Object.entries(chart.kua.directions.unfavorable).map(([key, dir]) => (
                      <div key={key} style={{ border: '2px solid #e03131', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#fff5f5' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#e03131' }}>{dir.name} ({dir.direction})</div>
                        <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600 }}>{dir.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pilar Keberuntungan 10 Tahun (Da Yun) */}
          {chart.luck_cycles && chart.luck_cycles.length > 0 && (() => {
            const currentYear = new Date().getFullYear();
            const elChToEn: Record<string, string> = { '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' };
            const dayPillar = chart.pillars?.find(p => p.position === 'day');
            const dmElement = dayPillar?.element ? elChToEn[dayPillar.element] : null;
            const generates: Record<string, string> = { wood: 'fire', fire: 'earth', earth: 'metal', metal: 'water', water: 'wood' };
            const ctrls: Record<string, string> = { wood: 'earth', earth: 'water', water: 'fire', fire: 'metal', metal: 'wood' };
            const getRelation = (ce: string, dm: string): 'generates' | 'same' | 'draining' | 'controls' | 'resisted' => {
              if (ce === dm) return 'same';
              if (generates[ce] === dm) return 'generates';
              if (generates[dm] === ce) return 'draining';
              if (ctrls[ce] === dm) return 'controls';
              return 'resisted';
            };
            const getTrend = (ce: string, dm: string, strong: boolean, elements: any): { dir: string; label: string; color: string } => {
              const rel = getRelation(ce, dm);
              let isFav = strong
                ? (rel === 'controls' || rel === 'draining')
                : (rel === 'generates' || rel === 'same');
                
              // Advanced rule (Shi Shen Zhi Sha): 
              // If DM is Weak, but Power (controls DM) is excessive (> 2), Output (draining) becomes Favorable to counter Power.
              if (!strong && rel === 'draining') {
                const powerElement = Object.keys(ctrls).find(k => ctrls[k] === dm);
                if (powerElement && elements && elements[powerElement] > 2) {
                  isFav = true;
                }
              }

              if (isFav) return { dir: '▲', label: 'Naik', color: '#2b8a3e' };
              if (rel === 'controls' || rel === 'resisted') return { dir: '◆', label: 'Normal', color: '#f59f00' };
              return { dir: '▼', label: 'Turun', color: '#e03131' };
            };
            return (
              <div style={{ 
                width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
                padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
              }}>
                <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                  <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                    <FieldTimeOutlined style={{ marginRight: 8, color: '#1a202c' }} /> Pilar Keberuntungan 10 Tahun (Da Yun / Luck Cycles)
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
                    Siklus perjalanan energi hidup kamu setiap 10 tahun sekali.
                  </p>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
                  gap: '12px' 
                }}>
                  {chart.luck_cycles.map((cycle, idx) => {
                    const isActive = currentYear >= cycle.start_year && currentYear <= cycle.end_year;
                    const elColorMap: Record<string, string> = {
                      wood: '#2b8a3e', fire: '#e03131', earth: '#d9480f', metal: '#495057', water: '#1971c2'
                    };
                    const badgeColor = elColorMap[cycle.element.toLowerCase()] || '#1a202c';
                    const trend = dmElement ? getTrend(cycle.element.toLowerCase(), dmElement, chart.day_master_strength === 'Strong', chart.elements) : null;

                    return (
                      <div 
                        key={idx} 
                        style={{ 
                          border: isActive ? '3px solid #2b8a3e' : '2px solid #1a202c', 
                          borderRadius: '10px', 
                          padding: '12px', 
                          backgroundColor: isActive ? '#ebfbee' : '#f8f9fa',
                          boxShadow: isActive ? '3px 3px 0 #2b8a3e' : '2px 2px 0 #1a202c',
                          position: 'relative',
                          textAlign: 'center'
                        }}
                      >
                        {isActive && (
                          <span style={{ 
                            position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                            backgroundColor: '#2b8a3e', color: '#ffffff', fontSize: '0.65rem', fontWeight: 900,
                            padding: '2px 8px', borderRadius: '12px', whiteSpace: 'nowrap', border: '1px solid #1a202c'
                          }}>
                            <><FireTwoTone twoToneColor="#fa541c" style={{ marginRight: 8 }} /> PERIODE AKTIF</>
                          </span>
                        )}
                        {trend && (
                          <div style={{
                            position: 'absolute', top: 0, left: 0, width: '6px', height: '100%',
                            backgroundColor: trend.color, borderRadius: '8px 0 0 8px'
                          }} />
                        )}
                        <div style={{ fontWeight: 900, fontSize: '0.85rem', color: '#1a202c', marginTop: isActive ? '4px' : '0' }}>
                          Usia {cycle.start_age}-{cycle.end_age} Thn
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 700, marginBottom: '6px' }}>
                          ({cycle.start_year}-{cycle.end_year})
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 950, color: badgeColor }}>
                          {cycle.stem}{cycle.branch}
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#4a5568', marginTop: '2px' }}>
                          {cycle.pinyin}
                        </div>
                        {trend && (
                          <div style={{
                            marginTop: '8px', padding: '4px 12px', borderRadius: '8px',
                            backgroundColor: trend.color + '1a',
                            border: '2px solid ' + trend.color,
                            color: trend.color, fontWeight: 900, fontSize: '0.75rem',
                            display: 'inline-block', letterSpacing: '0.5px'
                          }}>
                            {trend.dir} {trend.label}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#868e96', borderTop: '1px solid #dee2e6', paddingTop: '10px' }}>
                  <span style={{ color: '#2b8a3e' }}>▲ Naik</span>
                  <span style={{ color: '#f59f00' }}>◆ Normal</span>
                  <span style={{ color: '#e03131' }}>▼ Turun</span>
                  <span style={{ color: '#868e96' }}>— relatif terhadap Day Master ({chart.day_master_strength === 'Strong' ? 'Kuat' : 'Lemah'})</span>
                </div>
              </div>
            );
          })()}

          {/* Potensi Penyakit & Kesehatan */}
          {chart.career_health?.details?.penyakit && chart.career_health.details.penyakit.length > 0 && (
            <div style={{ 
              width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#e03131' }}>
                  <HeartTwoTone twoToneColor="#e03131" style={{ marginRight: 8 }} /> Potensi Penyakit & Panduan Kesehatan
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {chart.career_health.details.penyakit.map((item, idx) => (
                  <div key={idx} style={{ border: '2px solid #e03131', borderRadius: '10px', padding: '14px', backgroundColor: '#fff5f5', boxShadow: '2px 2px 0 #1a202c' }}>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 650, whiteSpace: 'pre-line' }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ⬇️ DISCLAIMER */}
          <div style={{
            marginTop: '8px',
            padding: '14px 18px',
            borderRadius: '10px',
            border: '2px dashed #b08968',
            backgroundColor: '#fdf6ee',
            fontSize: '0.78rem',
            color: '#7a5a2a',
            fontWeight: 600,
            lineHeight: '1.7',
            whiteSpace: 'pre-line',
            width: '100%',
            maxWidth: '100%',
            boxShadow: '1px 1px 0 #b08968'
          }}>
            <div style={{
              fontWeight: 800,
              fontSize: '0.75rem',
              color: '#9a7a4a',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px'
            }}>
              <SafetyCertificateTwoTone twoToneColor="#b08968" style={{ marginRight: 6 }} /> DISCLAIMER
            </div>
            <p style={{ margin: 0 }}>
              {`Menurut kepercayaan Cina Kuno Baik buruknya seseorang ditentukan oleh 3 Hal :
1. Langit (Sang Pencipta)
2. Bumi (Alam Semesta)
3. Manusia (Diri Sendiri)
Jika seseorang dikatakan buruk bisa jadi terhindar dari nasib buruknya, sebaliknya jika seseorang dikatakan baik bisa saja bernasib buruk.
Semua tergantung dari prinsip keseimbangan dari 3 hal tersebut.`}
            </p>
          </div>
          </div>
          ) : activeTabKey === '2' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>
            {/* 🔮 PEMBANDING TANGGAL CUSTOM (CUSTOM TRANSIT COMPATIBILITY) */}
            <div style={{ 
              width: '100%', 
              maxWidth: '100%', 
              border: '3px solid #1a202c', 
              borderRadius: '12px', 
              padding: '20px', 
              backgroundColor: '#ffffff', 
              boxShadow: '4px 4px 0 #1a202c',
              marginTop: '10px'
            }}>
            <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
              <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                <ProjectTwoTone style={{ marginRight: 8 }} /> Cek Keselarasan Diri dengan Tanggal Pilihan
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
                Bandingkan energi bagan lahir kamu dengan tanggal custom pilihanmu (misalnya tanggal penting, hari baik, atau hari tertentu).
              </p>
            </div>

            {compareError && (
              <Alert message={compareError} type="error" showIcon style={{ marginBottom: '20px', border: '2px solid #e03131', borderRadius: '8px' }} />
            )}

            <Form
              form={compareForm}
              layout="vertical"
              initialValues={{
                dob: dayjs(),
                tob: dayjs('12:00', 'HH:mm'),
                timezone: form.getFieldValue('timezone') || 'Asia/Jakarta'
              }}
              onFinish={handleCompare}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Pembanding</span>} name="dob" rules={[{ required: true, message: 'Silakan pilih tanggal pembanding dalam 30 hari ke depan' }]}>
                    <DatePicker inputReadOnly needConfirm disabledDate={(current) => current && (current.isBefore(dayjs(), 'day') || current.isAfter(dayjs().add(30, 'day'), 'day'))} style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Pembanding</span>} name="tob" rules={[{ required: !compareUnknownTob }]}>
                    <TimePicker inputReadOnly
                      format="HH:mm" 
                      disabled={compareUnknownTob}
                      style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} 
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu (Timezone)</span>} name="timezone" rules={[{ required: true }]}>
                    <Select style={{ height: '40px' }}>
                      <Select.Option value="Asia/Jakarta">WIB - Asia/Jakarta (GMT+7)</Select.Option>
                      <Select.Option value="Asia/Makassar">WITA - Asia/Makassar (GMT+8)</Select.Option>
                      <Select.Option value="Asia/Jayapura">WIT - Asia/Jayapura (GMT+9)</Select.Option>
                      <Select.Option value="Asia/Singapore">Singapore (GMT+8)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir Tidak Diketahui?</span>}>
                    <Switch checked={compareUnknownTob} onChange={setCompareUnknownTob} style={{ background: compareUnknownTob ? '#2b8a3e' : '#ced4da' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary"
                htmlType="submit"
                loading={compareLoading}
                block
                style={{
                  height: '42px',
                  background: '#fab005',
                  borderColor: '#1a202c',
                  color: '#1a202c',
                  fontWeight: 900,
                  fontSize: '0.95rem',
                  boxShadow: '3px 3px 0 #1a202c',
                  borderRadius: '10px',
                  marginTop: '6px'
                }}
              >
                <ProjectTwoTone style={{ marginRight: 8 }} /> Hitung Keselarasan Tanggal
              </Button>
            </Form>

            {/* Hasil Pembandingan */}
            {compareResult && (
              (() => {
                const lbl = (compareResult.bazi_synergy_label || '').toUpperCase();
                let bg = '#ffffe6'; let color = '#7a7400'; // AVERAGE
                if (lbl === 'VERY GOOD') { bg = '#e8f4fd'; color = '#0071C1'; } // VERY GOOD (Biru)
                if (lbl === 'GOOD') { bg = '#f0f9ec'; color = '#2d7a1e'; } // GOOD (Hijau)
                if (lbl === 'CHALLENGE') { bg = '#ffe6ff'; color = '#b300b3'; } // CHALLENGE
                if (lbl === 'BAD') { bg = '#ffe6ee'; color = '#FF0068'; } // BAD

                return (
                  <div style={{ 
                    marginTop: '24px', 
                    padding: '20px', 
                    borderRadius: '12px', 
                    border: '3px solid #1a202c', 
                    backgroundColor: bg,
                    boxShadow: '3px 3px 0 #1a202c'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <strong style={{ fontSize: '1rem', color: '#1a202c' }}>Hasil Keselarasan Energi:</strong>
                      <div style={{ fontSize: '2rem', fontWeight: 950, color: color, marginTop: '6px' }}>
                        {Math.round(Number(compareResult.bazi_synergy_score || 0))}% <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>({compareResult.bazi_synergy_label})</span>
                      </div>

                      {compareResult.strategy_guide?.narasi && (
                        <div style={{
                          marginTop: '16px',
                          marginBottom: '20px',
                          padding: '16px',
                          borderRadius: '10px',
                          border: '2px solid #1a202c',
                          backgroundColor: '#ffffff',
                          boxShadow: '2px 2px 0 #1a202c',
                          fontSize: '0.85rem',
                          color: '#2d3748',
                          fontWeight: 600,
                          lineHeight: '1.7',
                          whiteSpace: 'pre-line'
                        }}>
                          <div style={{
                            fontWeight: 900,
                            fontSize: '0.8rem',
                            color: '#1a202c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span><FileTextTwoTone twoToneColor="#1890ff" style={{ marginRight: 4 }} /> Narasi Fase {compareResult.bazi_synergy_label}</span>
                          </div>
                          {compareResult.strategy_guide.narasi}
                        </div>
                      )}
                    </div>



                    {compareChartData && (
                      <>
                        <Button 
                          type="default" 
                          onClick={() => setIsCompareChartVisible(true)}
                          style={{ 
                            fontWeight: 800, background: '#ffffff', color: '#1a202c', 
                            borderRadius: '8px', height: '42px', fontSize: '0.9rem',
                            width: '100%',
                            border: '2px solid #1a202c',
                            boxShadow: '2px 2px 0 #1a202c'
                          }}
                        >
                          <EyeTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Lihat Bagan Pembanding
                        </Button>
                        <Modal
                          title={<span style={{ fontWeight: 900, fontSize: '1.2rem' }}>Bagan BaZi Pembanding</span>}
                          open={isCompareChartVisible}
                          onCancel={() => setIsCompareChartVisible(false)}
                          footer={null}
                          width={800}
                          centered
                          bodyStyle={{ padding: '20px 0' }}
                        >
                          <div style={{ overflowX: 'auto', width: '100%', padding: '0 20px' }}>
                            <div style={{ minWidth: '600px' }}>
                              <BaziChartGrid chart={compareChartData} unknownTob={Boolean(compareChartData.unknown_tob)} />
                            </div>
                          </div>
                        </Modal>
                      </>
                    )}

                    {/* DISCLAIMER - Keselarasan Tanggal Custom */}
                    <div style={{
                      marginTop: '16px',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      border: '2px dashed #b08968',
                      backgroundColor: '#fdf6ee',
                      fontSize: '0.78rem',
                      color: '#7a5a2a',
                      fontWeight: 600,
                      lineHeight: '1.7',
                      whiteSpace: 'pre-line',
                      width: '100%',
                      maxWidth: '100%',
                      boxShadow: '1px 1px 0 #b08968'
                    }}>
                      <div style={{
                        fontWeight: 800,
                        fontSize: '0.75rem',
                        color: '#9a7a4a',
                        marginBottom: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}>
                        <SafetyCertificateTwoTone twoToneColor="#b08968" style={{ marginRight: 6 }} /> DISCLAIMER
                      </div>
                      <p style={{ margin: 0 }}>
              {`Menurut kepercayaan Cina Kuno Baik buruknya seseorang ditentukan oleh 3 Hal :
1. Langit (Sang Pencipta)
2. Bumi (Alam Semesta)
3. Manusia (Diri Sendiri)
Jika seseorang dikatakan buruk bisa jadi terhindar dari nasib buruknya, sebaliknya jika seseorang dikatakan baik bisa saja bernasib buruk.
Semua tergantung dari prinsip keseimbangan dari 3 hal tersebut.`}
                      </p>
                    </div>

                  </div>
                );
              })()
            )}
          </div>
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
export default PersonalChart;
